import { IReviewConf } from "~/interfaces";
import Dialog from "~/ui/dialog";
import { node, icon, text } from "~/util/dom";
import StarsBanner from "~/ui/stars-banner";
import { store } from "~/state/store";
import { watch } from "~/state/util";
import { setReviewPopupState } from "~/state/review/state";
import { IAlterData, IStarsInterval } from "./interfaces";
import { getFirmLayout } from "~/util/review";
import { CONFIG } from "~/config";
import { _ } from "~/i18n";
import { IReviewState } from "~/state/review/interfaces";
import { description, getIcon } from "~/util/providers/poiagg";

type TAlternatives = Array<IAlterData>;
type TClickCb = (source: string, id: number | string) => void;

interface ILogData {
	source?: string;
	position?: string;
}

function log(name: string, reviewData: IReviewConf, data: {[key: string] : string | number | boolean }) {
	Mapy.getComponent("logger").log(name, {
		firmTwin: reviewData.isTwin ? 1 : 0,
		position: "top",
		// eslint-disable-next-line camelcase
		rating_layout: getFirmLayout(reviewData.review, reviewData.booking, reviewData.myReview, reviewData.isFirm),
		source: "recenze-poi",
		sourcePoi: reviewData.source,
		type: "recenze",
		typeid: reviewData.typeId,
		section: "reviews",
		...data,
	});
}

function buildDialog(closeFc: () => void, starsData: IStarsInterval) {
	const dialog = new Dialog({
		classes: ["firm-review-popup", "alternatives"],
		title: `${_(`review.alternatives.title${starsData ? ".stars" : ""}`)}:`,
		onClose: () => {
			closeFc();
		},
	});

	if (starsData) {
		const starsBanner = new StarsBanner({
			percentage: starsData.percent,
		});

		dialog.dom.title.before(starsBanner.container);
	}

	document.body.style.overflow = "hidden";
	dialog.open();

	return dialog;
}

function buildListItem(data: IAlterData|IReviewConf, clickCb: TClickCb) {
	const item = node("li");
	const arrow = icon("forward");
	const container = node("p");
	const title = node("b", {}, data.title);
	const desc = text(data.typeName);
	const iconSpan = node("span");
	const poiIcon = getIcon(data.typeId, {
		scale: 2,
	});

	if (poiIcon) {
		iconSpan.appendChild(node("img", { src: poiIcon, srcset: `${poiIcon} 1x, ${poiIcon.replace("scale=2", "scale=3")} 2x` }));
	}

	container.append(title, desc);
	item.append(iconSpan, container, arrow);

	item.addEventListener("click", () => {
		clickCb(data.source, data.id);
	}, false);

	return item;
}

function buildList(data: IReviewConf, alternatives: TAlternatives, clickCb: TClickCb, starsData?: IStarsInterval, logData: ILogData = {}) {
	const list = node("ul");

	list.append(buildListItem(data, (source, id) => {
		clickCb(source, id);
		log("click", data, {
			text: "alternativePoi",
			index: 1,
			position: starsData ? "bottom" : "top",
			...logData,
		});
	}));

	alternatives.forEach((item, index) => {
		if (item.title && item.typeId) {
			list.append(buildListItem(item, (source, id) => {
				clickCb(source, id);
				log("click", data, {
					text: "alternativePoi",
					index: index + 2,
					position: starsData ? "bottom" : "top",
					...logData,
				});
			}));
		}
	});

	return list;
}

export function showAlternativesPOI(data: IReviewConf, alternatives: TAlternatives = [], starsData?: IStarsInterval, logData: ILogData = {
	source: "recenze-poi",
}): Promise<boolean> {
	return new Promise(resolve => {
		if (alternatives.length) {
			description(alternatives, {
				lang: [CONFIG.defaultLang],
			}).then(poisDescription => {
				let numOfAlternativesDesc = 0;

				poisDescription.poi.forEach(poi => {
					for (let index = 0; index < alternatives.length; index++) {
						if (alternatives[index].id === poi.id && alternatives[index].source === poi.source && poi.title && poi.typeId) {
							numOfAlternativesDesc++;
							alternatives[index] = {
								...alternatives[index],
								title: poi.title,
								typeId: poi.typeId,
								typeName: poi.typeName,
							};
						}
					}
				});

				if (numOfAlternativesDesc) {
					const dialog = buildDialog(() => {
						store.dispatch(setReviewPopupState({
							alternatives: false,
						}));
						resolve(false);
						log("reviews_form_close", data, {
							origin: "alternativesDialogue",
							position: starsData ? "bottom" : "top",
							...logData,
						});
					}, starsData);

					log("alternatives_dialogue_show", data, {
						position: starsData ? "bottom" : "top",
						...logData,
					});

					const watcher = watch<IReviewState>(store.getState, "reviewPopupState");
					const unsubscribe = store.subscribe(watcher(newValue => {
						if (newValue.edit || newValue.create || !newValue.alternatives) {
							dialog.close();
							unsubscribe();
						}
					}));

					store.dispatch(setReviewPopupState({
						alternatives: true,
					}));

					dialog.dom.content.append(buildList(data, alternatives, (source, id) => {
						if (source === data.source && id === data.id) {
							store.dispatch(setReviewPopupState({
								alternatives: false,
							}));
							resolve(true);
						} else {
							resolve(false);
							Mapy.getComponent("detail").setState({
								source,
								id,
								show: false,
								openReviewPopup: starsData || {
									stars: 0,
									percent: 0,
								},
							}).then(() => {
								store.dispatch(setReviewPopupState({
									alternatives: false,
								}));
								Mapy.getComponent("history").save();
							});
						}
					}, starsData, logData));
				} else {
					resolve(true);
				}
			}, error => {
				// eslint-disable-next-line no-console
				console.log(error);
				resolve(true);
			});
		} else {
			resolve(true);
		}
	});
}
